import isEmpty from 'lodash/isEmpty'
import { useState } from 'react'
import Filters from '../components/Filters'
import ProductGrid from '../components/ProductGrid'
import Layout, { LayoutHeaderSlot } from '../layout/Layout'
import Subheader from '../components/Subheader'
import { useDebouncedCallback } from 'use-debounce'
import useLocalStorageState from 'use-local-storage-state'
import ColumnChooserPopover from '../components/ColumnChooserPopover'
import CommonModal from '../components/CommonModal'
import AsinManagementModal from '../containers/AsinManagementModal'
import AsinsModal from '../containers/AsinsModal'
import RegionManager from '../components/RegionManager'
import NotificationBar from '../components/NotificationBar'
import { useColumnSettingsLoader } from '../hooks/useColumnSettingsSync'

// Reihenfolge spiegelt die Spalten-Definition in ProductGrid (allColumns):
// nach einem Reset (keine gespeicherte Order) zeigt der Column Chooser
// die Liste in GENAU dieser Reihenfolge an.
const COLUMNS = [
  ['amz_img', 'Amazon img'],
  ['actions', 'Befehle'],
  ['keepa_chart_dk', 'Chart'],
  ['flags', 'Flaggen'],
  ['margin', 'Marge'],
  ['roi', 'Roi'],
  ['profit', 'Profit'],
  ['salesrank', 'BSR'],
  ['percent_bsr', 'BSR%'],
  ['asin', 'Asin'],
  ['supplier_url', 'Supplier'],
  ['name', 'Title'],
  ['ean', 'EAN'],
  ['sellprice', 'Sellprice'],
  ['confirmed_sales', 'Conf. Sales'],
  ['salesdrops', 'Salesdrops'],
  ['variations', 'Variations'],
  ['buyprice', 'Buyprice'],
  ['timestamp', 'Timestamp'],
]

const application = 'pricealerts'

export default function Preiswecker() {
  let [regions, setRegions] = useLocalStorageState('selected-regions-alerts', {
    defaultValue: ['de']
  })
  let [search, setSearch] = useState('')
  let onSearchDebounced = useDebouncedCallback((value) => setSearch(value), 500)
  // Kontrollierter Wert nur fuer die Such-Pille (Clear-Button);
  // das eigentliche Suchen laeuft weiter debounced ueber setSearch.
  let [searchText, setSearchText] = useState('')
  const handleSearch = (value) => {
    setSearchText(value)
    onSearchDebounced(value)
  }

  // let [isAsinsModalOpen, setIsAsinsModalOpen] = useState(false)
  let [modalOpen, setModalOpen] = useState()

  let [hiddenColumnIds, setHiddenColumnIds] = useLocalStorageState(`${application}-col-hidden`, {
    defaultValue: []
  })

  let [columnOrder, setColumnOrder] = useLocalStorageState(`${application}-col-order`)
  // Gleicher Key wie in ProductGrid (use-local-storage-state synct) --
  // der Ansicht-Switch lebt jetzt im ColumnChooserPopover.
  let [viewMode, setViewMode] = useLocalStorageState(`${application}-view-mode`, {
    defaultValue: 'table'
  })
  let [columnSizing, setColumnSizing] = useLocalStorageState(`${application}-col-sizing`, {
    defaultValue: {}
  })

  useColumnSettingsLoader({
    module: application,
    setColumnOrder,
    setHiddenColumnIds,
    setColumnSizing,
  })

  let [tutorialModal, setTutorialModal] = useState(false)

  /*
  handleAsinsClose
    asinsModal = false
    showAsinsManagement = true

  handleAsinsModal
    asinsModal = true
    showAsinsManagement = true

  */

  return (
    <Layout scrollWithHeader>
      <section className="overflow-hidden w-full h-full flex flex-col">
        {/* Einziger Scroller der Seite (vertikal + horizontal): die EDGE-Leiste
            (LayoutHeaderSlot) scrollt vertikal mit weg, das Seiten-Chrome bleibt
            per sticky left beim Quer-Scrollen stehen, die thead-Spaltennamen
            kleben per sticky top am oberen Rand. */}
        <div className="flex-1 min-h-0 flex flex-col overflow-auto no-h-scrollbar" style={{ overflowAnchor: 'none' }}>
          <LayoutHeaderSlot />
          <div className="sticky left-0 z-20 bg-background">
        {/* Stack-Layout (Redesign): Modulname oben, Control-Leiste darunter
            (Suche · Märkte · Pings · Ansicht · [Abstand] · Asins bearbeiten).
            Grosszuegiges Spacing -> Titel / Controls / Filter wirken als
            getrennte Sektionen. */}
        <header className="px-4 pt-4 pb-5">
          <Subheader date>
            Preis<em>wecker</em>
          </Subheader>

          {/* Toolbar-Bar (Redesign Richtung 6): Controls in eigenem umrandeten
              Balken. Suche fuellt links (gekappt), Buttons + CTA rechts. */}
          <div className='mt-5 flex flex-wrap items-center gap-2.5 p-2.5 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-bgColor dark:bg-white/[0.02]'>
            {/* Such-Pille — fuellt den Platz links */}
            <div className="relative flex-1 min-w-[220px] max-w-[380px] group">
              <i className="bx bx-search absolute left-3 top-1/2 -translate-y-1/2 text-base text-neutral-400 pointer-events-none group-focus-within:text-primary"></i>
              <input
                type="text"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Produkt, ASIN oder EAN suchen"
                className="w-full h-9 pl-9 pr-9 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-background text-sm text-textTitle placeholder:text-neutral-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
              />
              {searchText && (
                <button
                  type="button"
                  onClick={() => handleSearch('')}
                  aria-label="Leeren"
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-6 w-6 rounded text-neutral-400 hover:text-textTitle"
                >
                  <i className="bx bx-x text-lg leading-none"></i>
                </button>
              )}
            </div>

            <span className='flex-1'></span>

            <RegionManager regions={regions} setRegions={setRegions} />
            <NotificationBar application={application} />

            <ColumnChooserPopover
              application='pricealerts'
              // Card View hat keine konfigurierbaren Spalten -> Sektion weg
              columns={viewMode === 'cards' ? null : COLUMNS}
              viewOptions={{
                value: viewMode,
                onChange: setViewMode,
                options: [
                  { value: 'table', label: 'Multiview' },
                  { value: 'cards', label: 'Card View' },
                ],
              }}
              onReset={(!isEmpty(hiddenColumnIds) || !!columnOrder || !isEmpty(columnSizing)) && (() => {
                setHiddenColumnIds([])
                setColumnOrder(null)
                setColumnSizing({})
              })}
            >{({ setIsOpen, isOpen }) =>
              <button
                type='button'
                onClick={() => setIsOpen(val => !val)}
                className={`relative inline-flex items-center gap-2 h-9 px-3 rounded-lg border text-sm font-medium transition-colors ${isOpen ? 'border-primary text-primary bg-secondary' : 'border-neutral-200 dark:border-neutral-700 text-textTitle bg-background hover:border-primary'}`}
              >
                <i className='bx bx-slider-alt text-[17px] leading-none' aria-hidden></i>
                Ansicht
                <i className={`bx bx-chevron-down text-base leading-none -ml-0.5 ${isOpen ? 'text-primary' : 'text-neutral-400'}`} aria-hidden></i>
              </button>}
            </ColumnChooserPopover>

            {/* Primär-CTA (Mint-Gradient) */}
            <button
              type='button'
              onClick={() => setModalOpen('management')}
              className='inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-gradient-to-br from-primary to-main text-white dark:text-black text-sm font-semibold hover:brightness-105 active:scale-[.98] transition'
            >
              <i className="bx bx-plus text-[18px] leading-none"></i>
              Asins bearbeiten
            </button>
          </div>
        </header>
        {/* px-4 = gleiche linke Kante wie Titel + Toolbar-Bar (header px-4) */}
        <div className='px-4 pb-2'>
          <Filters application='pricealerts' onChange={() => { }} />
        </div>
          </div>
          <ProductGrid
            application={application}
            regions={regions}
            search={search}
            hiddenColumnIds={hiddenColumnIds}
            outerScroll
          />
        </div>
      </section>

      <CommonModal
        width={(modalOpen === 'management' || modalOpen === 'blacklist') ? '95vw' : (application === 'pricealerts' ? '80vw' : '')}
        maxWidth={(modalOpen === 'management' || modalOpen === 'blacklist') ? '1500px' : (application === 'pricealerts' ? '1000px' : '')}
        isOpen={!!modalOpen}
        setIsOpen={() => setModalOpen('asins')}
        overflowY={false}
        closeModal={() => setModalOpen()}>
        {
          modalOpen === 'management' ? (
            <AsinManagementModal
              mode='watchlist'
              onClose={() => setModalOpen()}
              onShowAsinsModal={() => {
                setModalOpen('asins')
              }}
              onSwitchToBlacklist={() => setModalOpen('blacklist')}
            />
          ) :
          modalOpen === 'blacklist' ? (
            <AsinManagementModal
              mode='blacklist'
              onClose={() => setModalOpen()}
              onSwitchToWatchlist={() => setModalOpen('management')}
            />
          ) :
          modalOpen === 'asins' ? (
            <AsinsModal
              closeModal={() => {
                setModalOpen('management')
              }}
              asinsModalHeader='Asins'
              priceAlertInfoName='price_alert_info_pricealerts'
              addPriceAlertName='add_price_alert_pricealerts'
              deletePriceAlertName='delete_price_alert_pricealerts'
              asinsModalSubHeader={<>
                <p>
                  Lade eine CSV Datei mit Asins hoch um automatisch <br />
                  Preiswecker zu stellen. Noch nie gemacht?{" "}
                  <a
                    className="text-primary"
                    href="https://www.loom.com/share/8b9413f9308246d5b40a5e16a633cb95"
                    target="_blank"
                    rel="noreferrer">
                    Tutorial
                  </a>
                </p>
              </>}
              isTutorialModal={true}
              setTutorialModal={setTutorialModal}
              tutorialModal={tutorialModal}
            />
          ) : null
        }
      </CommonModal>
    </Layout>
  )
}
